import type { DisplayContext, DisplayZone } from './types';

export interface ResolveDisplayZoneInput {
    /** The kind of time being shown — drives whether dual display applies. */
    context: DisplayContext;
    /** The auction-house / site zone (IANA). Always the fallback. */
    siteZone: string;
    /** The viewer's device zone (IANA), when known. */
    viewerZone?: string | null;
    /** The frozen zone-of-record (IANA) for `record` context, when stored. */
    recordZone?: string | null;
}

/**
 * Decide which zone(s) a time should display in.
 *
 * - `attending` → lead with the **site / auction** zone, show the **viewer's**
 *                 alongside. ("Live sale 9:00 AM PT (11:00 AM your time)")
 * - `record`    → a single frozen zone-of-record (or the site zone). No dual display.
 *
 * **The site zone always leads.** BUG-1581 retired the `planning` variant, which
 * led with the viewer's — see {@link DisplayContext} and ADR 024 § 3. There is
 * deliberately no branch here selecting a leading zone: one rule, expressed once.
 *
 * Dual display only kicks in when the two zones actually differ; a local viewer
 * sees one zone in either context.
 *
 * @sideEffect none — pure.
 */
export function resolveDisplayZone(input: ResolveDisplayZoneInput): DisplayZone {
    const { context, siteZone } = input;
    const viewerZone = input.viewerZone || null;

    if (context === 'record') {
        return { primary: input.recordZone || siteZone, secondary: null, showDual: false };
    }

    const showDual = viewerZone != null && viewerZone !== siteZone;

    return { primary: siteZone, secondary: showDual ? viewerZone : null, showDual };
}
